import { useState, useEffect, lazy, Suspense } from "react";

// Skeleton loader minimal - CLS optimized with contain
export const SectionSkeleton = ({ height = "400px" }) => (
  <div 
    className="bg-gray-100 dark:bg-gray-800" 
    style={{ 
      height, 
      minHeight: height, 
      maxHeight: height,
      contain: 'strict',
      contentVisibility: 'auto'
    }}
    aria-hidden="true"
  />
);

// Load component after idle or timeout
export const IdleLoad = ({ children, fallback = null, delay = 0 }) => {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    let cancelled = false;
    
    const load = () => {
      if (!cancelled) setShouldLoad(true);
    };

    if (delay > 0) {
      const timer = setTimeout(load, delay);
      return () => { cancelled = true; clearTimeout(timer); };
    }

    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(load, { timeout: 2000 });
      return () => { cancelled = true; cancelIdleCallback(id); };
    } else {
      const timer = setTimeout(load, 100);
      return () => { cancelled = true; clearTimeout(timer); };
    }
  }, [delay]);

  return shouldLoad ? children : fallback;
};

// Load on scroll intersection
export const LazySection = ({ children, fallback = null, rootMargin = "200px" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [ref, setRef] = useState(null);

  useEffect(() => {
    if (!ref) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0 }
    );

    observer.observe(ref);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return (
    <div ref={setRef}>
      {isVisible ? children : fallback}
    </div>
  );
};

// Load on user interaction (scroll/click/touch)
export const InteractionLoad = ({ children, fallback = null }) => {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;

    const load = () => setShouldLoad(true);
    
    // Load on any interaction
    const events = ['scroll', 'touchstart', 'click', 'mousemove', 'keydown'];
    events.forEach(e => window.addEventListener(e, load, { once: true, passive: true }));
    
    // Fallback after 3s
    const timer = setTimeout(load, 3000);

    return () => {
      events.forEach(e => window.removeEventListener(e, load));
      clearTimeout(timer);
    };
  }, [shouldLoad]);

  return shouldLoad ? children : fallback;
};

export default LazySection;
